import { useEffect, useState } from 'react';
import { TSelectItemDialogState, TFolder, TPath, SelectItemDialog, TItem } from '@modules/items';
import { FULL_PATH_SETUP_ITEM } from '@modules/items';
import { FULL_PATH_REFERENCE_DETECTION_ITEM } from '../../constants';
import ReferenceDetectionPreview from './ReferenceDetectionPreview';
import LockedUsersDialog, { TLockedUsersDialogState } from '../shared/dialogs/LockedUsersDialog';
import { useCancelReferenceDetection, useCreateReferenceDetection } from '../../api/references.api';
import { useSearchParams } from '@shared/hooks';
import { openDialog } from '../../helpers/dialogs.helper';
import DetectionScaffold from '../shared/layout/DetectionScaffold';
import StartDetectionButton from '../shared/layout/StartDetectionButton';
import { TReferenceDetectionSelectableItemType } from '../../types/references.types';

type TProps = {
  folder: TFolder;
};

const ReferenceDetectionContent = ({ folder }: TProps) => {
  const { change } = useSearchParams();
  const [selectedItems, setSelectedItems] = useState<TItem[]>([]);

  const rootItemId = folder._id;

  const [runId, setRunId] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (!runId) change('wide', undefined); // clean up
  }, [runId]);

  // "fullPath" for the FoldersNavDrawer
  const fullPath: TPath = [
    { _id: rootItemId, name: folder.name, accessRight: folder.accessRight },
    FULL_PATH_SETUP_ITEM, // Setup
    FULL_PATH_REFERENCE_DETECTION_ITEM, // Reference detection
  ];

  const createMutation = useCreateReferenceDetection();
  const cancelMutation = useCancelReferenceDetection();

  // Dialog states
  const [lockedUsersDialogState, setLockedUsersDialogState] = useState<TLockedUsersDialogState>({
    isOpen: false,
    userIds: [],
  });

  const handleSelect = async (selectedItems: TItem[]) => {
    // use try-catch and `mutateAsync` to handle errors
    try {
      if (runId) await cancelMutation.mutateAsync({ runId });

      setRunId(undefined); // reset run-id to start fresh

      await createMutation.mutateAsync(
        {
          rootItemId,
          selectedItems: selectedItems.map((selectedItem) => ({
            itemId: selectedItem._id,
            itemType: selectedItem.type as TReferenceDetectionSelectableItemType,
          })),
        },
        {
          onSuccess: (data) => {
            if (data?.lockedUserIds) {
              setLockedUsersDialogState((prev) => ({ ...prev, isOpen: true, userIds: data.lockedUserIds }));
            } else {
              setSelectItemDialogState((prev) => prev && { ...prev, isOpen: false });
              setRunId(data.runId);
            }
          },
        }
      );

      setSelectedItems(selectedItems);
    } catch (error) {
      console.error('Error during reference detection selection:', error); // dev only
    }
  };

  const [selectItemDialogState, setSelectItemDialogState] = useState<TSelectItemDialogState | undefined>({
    isOpen: false,
    onSelect: handleSelect,
  });

  // reset both isOpen and onSelect to ensure the dialog is opened with the correct state (viewMode and runId values)
  const handleStart = () => {
    openDialog(setSelectItemDialogState, handleSelect);
  };

  return (
    <DetectionScaffold folder={folder} fullPath={fullPath} selectedValue="reference-detection">
      {/* dialogs */}
      <SelectItemDialog
        initialFolderId={rootItemId}
        initialSelectedItems={selectedItems}
        state={selectItemDialogState}
        setState={setSelectItemDialogState}
        multiSelect
        title="Select items for detection"
        actionText="Submit"
        navigatableFolderTypes={['NORMAL', 'COUNTRY']}
        selectableTypes={['FOLDER', 'DOCUMENT', 'FILE']}
      />
      <LockedUsersDialog state={lockedUsersDialogState} setState={setLockedUsersDialogState} />

      {/* main content area */}
      {!runId ? (
        <StartDetectionButton onStart={handleStart} />
      ) : (
        <ReferenceDetectionPreview
          runId={runId}
          setRunId={setRunId}
          handleStart={handleStart}
          setSelectedItems={setSelectedItems}
        />
      )}
    </DetectionScaffold>
  );
};

export default ReferenceDetectionContent;
